/**
 * Free-gift option picker for the cart drawer.
 *
 * Gift lines carry a `Select Options` button plus a <template> holding the
 * modal body for every colour the tier offers (see cart-drawer-line-item.liquid).
 * Opening clones that template into a <cart-gift-swap> element appended to
 * <body> — NOT into the drawer, because component-cart-drawer.js replaces
 * `.drawer__wrapper` innerHTML on every cart:change and would destroy an open
 * modal mid-interaction.
 *
 * Because every colour ships in the clone, switching colour is a class toggle:
 * no fetch, no latency, no loading state.
 *
 * The trigger listener is registered at module scope rather than on the element,
 * following cart-tier-rewards.js: the drawer re-renders its buttons constantly,
 * and a document-level listener survives that. It is also why this lives in its
 * own module — cart.js bails out of its own delegated handler on
 * `.cart-item--tier-gift` clicks (cart.js:349), so a handler added there would
 * never fire.
 *
 * The Size Guide button opens a chart popup nested inside this element rather
 * than appended to <body>: <cart-gift-swap> already sits at the highest
 * z-index the drawer may use (see the note in cart-gift-swap.css), so the
 * chart stacks within it instead of escalating past the mobile nav. Nesting
 * also means it is torn down with the picker, and lets the existing click,
 * Escape and focus-trap handlers own both layers — hence no second
 * document-level listener. Its image/title come straight from the trigger's
 * dataset, resolved server-side in cart-drawer-line-item.liquid from the
 * gift's tier metaobject (Size Chart Image field) — no client-side lookup.
 *
 * Reviews use the same Yotpo `bottomLine` widget as the PDP. Yotpo's global app
 * embed is disabled on this store — its runtime only loads from an app block on
 * product templates — so on collection/home/cart pages this module injects the
 * loader itself before initialising the widget.
 *
 * Submitting adds the chosen variant and then removes the old line. It still
 * carries _cart_tier_swapped, but nothing reads that any more — a gift line
 * with choices shows the same "Change selection" control whether it was
 * auto-added or picked here. The one-click "Claim Gift" path, for
 * single-default-variant tiers whose Auto Add Free Gift switch is off, is a
 * separate module: cart-tier-claim.js.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const SOURCE = 'cart-gift-swap';

/** Only one picker can be open at a time. */
let openInstance = null;

export class CartGiftSwap extends HTMLElement {
  constructor() {
    super();
    /** Open size chart layer, or null. Also marks which layer owns Escape. */
    this.sizeGuide = null;
    this.sizeGuideTrigger = null;
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onCartChange = this.onCartChange.bind(this);
    this.onSlidesScroll = this.onSlidesScroll.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);
    /* The line this picker belongs to may not survive the change — and the
       drawer re-renders underneath us either way. */
    document.addEventListener('cart:change', this.onCartChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('cart:change', this.onCartChange);
    this.detachSlideListeners();
    clearTimeout(this.ratingTimer);
    if (openInstance === this) {
      openInstance = null;
      releaseBodyScroll();
    }
  }

  /* ---------------------------------------------------------------- open */

  /**
   * @param {HTMLTemplateElement} template source of the modal body
   * @param {HTMLElement} trigger the button that opened it, for focus restore
   */
  render(template, trigger) {
    this.trigger = trigger;
    this.lineKey = trigger.dataset.lineKey || '';
    this.tierId = trigger.dataset.tierId || '';
    this.currentVariantId = trigger.dataset.currentVariantId || '';
    this.yotpoKey = trigger.dataset.yotpoKey || '';
    this.sizeLabels = readSizeLabels();
    this.errorLabel = trigger.dataset.errorLabel || '';
    this.isSubmitting = false;

    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('aria-label', trigger.textContent.trim());

    const overlay = document.createElement('div');
    overlay.className = 'gift-swap__overlay';
    overlay.setAttribute('data-gift-swap-close', '');

    const panel = document.createElement('div');
    panel.className = 'gift-swap__panel';

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'gift-swap__close';
    close.setAttribute('data-gift-swap-close', '');
    /* Translated copy travels on the trigger — the drawer renders its strings
       in Liquid rather than going through theme.strings. */
    close.setAttribute('aria-label', trigger.dataset.closeLabel || 'Close');
    close.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M9.06066 8.00022L13.7694 12.709L12.7088 13.7696L8 9.06088L3.29125 13.7696L2.23059 12.709L6.93934 8.00022L2.23128 3.29216L3.29194 2.2315L8 6.93956L12.7081 2.2315L13.7687 3.29216L9.06066 8.00022Z" fill="black"/></svg>';

    const body = document.createElement('div');
    body.className = 'gift-swap__body';
    body.appendChild(template.content.cloneNode(true));

    panel.append(close, body);
    this.append(overlay, panel);

    /* The trigger knows which variant the line currently holds; the template
       marks the matching colour. Fall back to the first choice if the line's
       product is no longer among the alternatives. */
    if (!this.activeChoice()) {
      const first = this.choices()[0];
      if (first) this.showChoice(first);
    }

    /* Covers the fallback above, whose colour carries no server-rendered
       selection. A no-op for the cart's own colour, which already has one. */
    this.ensureSizeSelected(this.activeChoice());

    /* Covers the same fallback: the template only eager-renders an iframe
       for is_current_choice, so a fallback-selected colour still needs one. */
    this.activateEmbed(this.activeChoice());

    this.syncSizeLabel();
    this.attachSlideListeners();
  }

  open() {
    openInstance = this;
    document.body.appendChild(this);
    lockBodyScroll();
    /* Next frame so the element is laid out before the transition starts. */
    requestAnimationFrame(() => this.classList.add('is-open'));

    const target =
      this.querySelector('.gift-swap__swatch.is-active') || this.querySelector(FOCUSABLE);
    if (target) target.focus({ preventScroll: true });

    this.refreshRating();
    /* Only now is the popup in the document, so this is the first point the
       wishlist app can see (and hydrate) its button. */
    this.syncWishlist();
  }

  close() {
    if (openInstance === this) openInstance = null;
    /* Drop the nested chart's state before the subtree goes with us, so a
       reused instance does not think a chart is still open. */
    this.sizeGuide = null;
    this.sizeGuideTrigger = null;
    releaseBodyScroll();
    this.stopMedia();
    const trigger = this.trigger;

    this.classList.remove('is-open');
    const done = () => {
      if (openInstance !== this) this.remove();
    };
    if (prefersReducedMotion()) {
      done();
    } else {
      this.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 350);
    }

    /* Only restore focus if the trigger is still in the document — a cart
       change may have re-rendered the drawer out from under it. */
    if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------- queries */

  choices() {
    return Array.from(this.querySelectorAll('[data-gift-choice]'));
  }

  activeChoice() {
    return this.querySelector('[data-gift-choice].is-active');
  }

  /* ------------------------------------------------------------- handlers */

  onClick(event) {
    /* Both size-guide branches come first: its overlay sits above the picker's
       own controls, so a click landing on it is never meant for them. */
    if (event.target.closest('[data-size-guide-close]')) {
      event.preventDefault();
      this.closeSizeGuide();
      return;
    }

    const sizeGuide = event.target.closest('[data-size-guide-trigger]');
    if (sizeGuide) {
      event.preventDefault();
      this.openSizeGuide(sizeGuide);
      return;
    }

    if (event.target.closest('[data-gift-swap-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const swatch = event.target.closest('[data-gift-swap-swatch]');
    if (swatch) {
      event.preventDefault();
      this.selectColor(swatch.dataset.productId);
      return;
    }

    const size = event.target.closest('[data-gift-swap-size]');
    if (size && !size.disabled) {
      event.preventDefault();
      this.selectSize(size);
      return;
    }

    const submit = event.target.closest('[data-gift-swap-submit]');
    if (submit) {
      event.preventDefault();
      this.submit(submit);
      return;
    }

    /* Thumbs and dots are the same control at two breakpoints. */
    const jump = event.target.closest('[data-gift-swap-thumb], [data-gift-swap-dot]');
    if (jump) {
      event.preventDefault();
      this.goToSlide(parseInt(jump.dataset.index, 10) || 0);
    }
  }

  onKeydown(event) {
    if (openInstance !== this) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      /* Innermost layer first — Escape on an open chart returns you to the
         picker rather than dismissing both. */
      if (this.sizeGuide) this.closeSizeGuide();
      else this.close();
      return;
    }

    if (event.key === 'Tab') this.trapFocus(event);
  }

  onCartChange() {
    /* Our own swap fires cart:change twice (add, then remove). Closing on the
       first would tear the modal down mid-flight and take any error message
       with it, so leave it to submit() to close once the swap has settled. */
    if (this.isSubmitting) return;

    /* The gift line — and the trigger — may be gone. Reopening from a stale
       template would show the wrong selection, so close instead. */
    this.close();
  }

  trapFocus(event) {
    /* Trap within the topmost layer, so Tab cannot reach the picker's controls
       behind an open size chart. */
    const scope = this.sizeGuide || this;
    const focusable = Array.from(scope.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ----------------------------------------------------------- size guide */

  /**
   * Chart popup for the gift's tier. Nested inside this element rather than
   * the drawer, which is replaced on every cart:change.
   * @param {HTMLElement} trigger the Size Guide button, for focus restore
   */
  openSizeGuide(trigger) {
    const chartImage = trigger.dataset.chartImage;
    const chartTitle = trigger.dataset.chartTitle;
    /* Liquid only renders the button when the tier has a chart image, so this
       is a guard against the field changing after the drawer was rendered. */
    if (!chartImage) return;

    if (this.sizeGuide) this.closeSizeGuide();
    this.sizeGuideTrigger = trigger;

    const wrap = document.createElement('div');
    wrap.className = 'size-guide';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('data-size-guide', '');

    const overlay = document.createElement('div');
    overlay.className = 'size-guide__overlay';
    overlay.setAttribute('data-size-guide-close', '');

    const panel = document.createElement('div');
    panel.className = 'size-guide__panel';

    const header = document.createElement('div');
    header.className = 'size-guide__header';

    const title = document.createElement('h3');
    title.className = 'size-guide__title';
    title.textContent = chartTitle || 'Size & Fit';
    /* Names the dialog without a separate aria-label to keep in step. */
    title.id = 'size-guide-title';
    wrap.setAttribute('aria-labelledby', title.id);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'size-guide__close';
    close.setAttribute('data-size-guide-close', '');
    /* Same convention as the picker's own close button: translated copy
       travels on the trigger rather than through theme.strings. */
    close.setAttribute('aria-label', trigger.dataset.closeLabel || 'Close');
    close.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M9.06066 8.00022L13.7694 12.709L12.7088 13.7696L8 9.06088L3.29125 13.7696L2.23059 12.709L6.93934 8.00022L2.23128 3.29216L3.29194 2.2315L8 6.93956L12.7081 2.2315L13.7687 3.29216L9.06066 8.00022Z" fill="black"/></svg>';

    header.append(title, close);

    const body = document.createElement('div');
    body.className = 'size-guide__body';

    const image = document.createElement('img');
    image.className = 'size-guide__img';
    image.alt = chartTitle || 'Size guide';
    image.loading = 'lazy';
    image.addEventListener('error', () => {
      image.hidden = true;
    });
    image.src = chartImage;
    body.appendChild(image);

    panel.append(header, body);
    wrap.append(overlay, panel);

    this.appendChild(wrap);
    this.sizeGuide = wrap;
    /* Next frame so the element is laid out before the transition starts. */
    requestAnimationFrame(() => wrap.classList.add('is-open'));

    close.focus({ preventScroll: true });
  }

  closeSizeGuide() {
    if (!this.sizeGuide) return;
    this.sizeGuide.remove();
    this.sizeGuide = null;

    const trigger = this.sizeGuideTrigger;
    this.sizeGuideTrigger = null;
    /* The picker stays open behind the chart, so the trigger is normally still
       there — but a colour swap can re-render it away. */
    if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------ selection */

  selectColor(productId) {
    const next = this.choices().find((choice) => choice.dataset.productId === productId);
    if (!next || next.classList.contains('is-active')) return;

    /* Carry the chosen size across, when the new colour offers it. */
    const wantedSize = this.selectedSizeValue();

    /* Before the old colour leaves the screen — otherwise a playing video keeps
       its audio running behind the new one. */
    this.stopMedia();
    this.showChoice(next);

    /* The template only eager-renders an iframe for is_current_choice — every
       other colour needs its embed created the first time it becomes active. */
    this.activateEmbed(next);

    this.ensureSizeSelected(next, wantedSize);

    this.syncSizeLabel();
    this.syncWishlist();
    this.attachSlideListeners();
    this.goToSlide(0);
    this.refreshRating();
  }

  showChoice(next) {
    this.choices().forEach((choice) => {
      const isNext = choice === next;
      choice.classList.toggle('is-active', isNext);
      choice.hidden = !isNext;
    });

    /* Every choice renders its own full swatch row, so the active state only
       needs syncing within the one now on screen. */
    next.querySelectorAll('[data-gift-swap-swatch]').forEach((swatch) => {
      const isActive = swatch.dataset.productId === next.dataset.productId;
      swatch.classList.toggle('is-active', isActive);
      if (isActive) {
        swatch.setAttribute('aria-current', 'true');
      } else {
        swatch.removeAttribute('aria-current');
      }
    });
  }

  selectSize(button, { silent = false } = {}) {
    const choice = button.closest('[data-gift-choice]');
    if (!choice) return;

    choice.querySelectorAll('[data-gift-swap-size]').forEach((size) => {
      const isSelected = size === button;
      size.classList.toggle('is-selected', isSelected);
      size.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.currentVariantId = button.dataset.variantId || '';
    if (!silent) {
      this.syncSizeLabel();
      this.syncWishlist();
    }
  }

  selectedSizeValue() {
    const selected = this.activeChoice()?.querySelector('[data-gift-swap-size].is-selected');
    return selected ? selected.dataset.value : '';
  }

  /**
   * Guarantee the given colour has a size selected.
   *
   * Only the colour currently in the cart is server-rendered with a selection
   * (the Liquid marks `is-selected` off `line_item.variant.id`), so every other
   * colour arrives with an empty size row — hence the fallback rather than
   * carrying a value across only when one happens to exist.
   *
   * A colour whose sizes are all sold out is left unselected on purpose:
   * `currentVariantId` feeds add-to-cart, and pointing it at a disabled variant
   * would be worse than an empty row the shopper can see is unavailable.
   *
   * @param {HTMLElement} choice the [data-gift-choice] to resolve within
   * @param {string} [preferredValue] size to keep from the previous colour
   */
  ensureSizeSelected(choice, preferredValue) {
    if (!choice) return;
    /* No size row at all — a single-variant gift. */
    if (!choice.querySelector('[data-gift-swap-size]')) return;

    const preferred = preferredValue
      ? choice.querySelector(
          `[data-gift-swap-size][data-value="${cssEscape(preferredValue)}"]:not([disabled])`
        )
      : null;

    const target =
      preferred ||
      choice.querySelector('[data-gift-swap-size].is-selected:not([disabled])') ||
      choice.querySelector('[data-gift-swap-size]:not([disabled])');

    if (target) this.selectSize(target, { silent: true });
  }

  /**
   * Expand a size code for display — "YL" -> "Youth/Large".
   * Unlisted codes come back unchanged, so values that are already words
   * ("SM/MD", "3/4", "10.5") are never mangled.
   */
  sizeLabel(code) {
    if (!code) return '';
    return this.sizeLabels.get(code.trim().toUpperCase()) || code;
  }

  /** Mirrors the selected size into the "Size <value>" heading. */
  syncSizeLabel() {
    const choice = this.activeChoice();
    if (!choice) return;
    const label = choice.querySelector('[data-gift-swap-size-value]');
    /* Heading only — the buttons keep their short codes, both so the grid
       stays compact and because ensureSizeSelected() matches on data-value. */
    if (label) label.textContent = this.sizeLabel(this.selectedSizeValue());
  }

  syncWishlist() {
    const choice = this.activeChoice();
    if (!choice) return;

    const button = choice.querySelector('[data-gift-swap-save] wishlist-button-product');
    if (!button) return;

    const variantId = this.chosenVariantId() || choice.dataset.variantId || '';
    if (!variantId) return;

    const unchanged = button.getAttribute('data-variant-id') === variantId;
    if (unchanged && button.dataset.giftSwapHydrated === variantId) return;

    button.setAttribute('data-variant-id', variantId);
    button.setAttribute('data-product-variant-id', variantId);
    button.dataset.giftSwapHydrated = variantId;

    try {
      window.dispatchEvent(new CustomEvent('wishlist:reinit', { detail: { variantId } }));
      document.dispatchEvent(new CustomEvent('swym:reinit', { detail: { variantId } }));

      [window.WishlistKing, window.WishlistHero, window.SwishWishlist, window.SwymClient].forEach(
        (sdk) => {
          if (!sdk) return;
          try {
            if (typeof sdk.reinit === 'function') sdk.reinit();
            if (typeof sdk.init === 'function') sdk.init();
            if (typeof sdk.render === 'function') sdk.render();
            if (typeof sdk.refresh === 'function') sdk.refresh();
          } catch (error) {
            /* one broken SDK must not stop the others */
          }
        }
      );

      setTimeout(() => {
        try {
          document.dispatchEvent(new CustomEvent('swish:ready'));
          document.dispatchEvent(new CustomEvent('wkh:ready'));
          document.dispatchEvent(new CustomEvent('page:updated'));
        } catch (error) {
          /* ignore */
        }
      }, 250);
    } catch (error) {
      /* ignore — the wishlist app is optional */
    }
  }

  /* --------------------------------------------------------------- submit */

  /**
   * Which variant the shopper has actually chosen.
   *
   * Prefers the selected size button, and falls back to the colour's own
   * default — a single-variant gift renders no size buttons at all, so
   * `currentVariantId` would still hold the colour we navigated away from.
   * Reading from the ACTIVE choice both times is what makes that safe.
   */
  chosenVariantId() {
    const choice = this.activeChoice();
    if (!choice) return '';

    const selected = choice.querySelector('[data-gift-swap-size].is-selected:not([disabled])');
    if (selected) return selected.dataset.variantId || '';

    /* A colour whose sizes are all sold out selects nothing — fall through to
       the default only when this colour has no size row at all. */
    if (choice.querySelector('[data-gift-swap-size]')) return '';
    return choice.dataset.variantId || '';
  }

  /**
   * Put the chosen variant in the cart.
   *
   * Two callers: a claimed gift line, which passes a lineKey and so gets its
   * old line replaced; and an unclaimed placeholder row, which passes none and
   * so is a plain add — the gift's first entry into the cart, since
   * cart-tier-rewards.js no longer auto-adds it.
   *
   * Add first, then remove — the opposite order to cart-tier-rewards.js's
   * applyPlan(). A background reconciler can afford to leave the cart briefly
   * giftless because it retries; a shopper pressing a button cannot. If the
   * remove fails instead, the leftover duplicate is cleaned up on the next
   * reconcile pass.
   */
  async submit(button) {
    if (this.isSubmitting) return;

    const variantId = this.chosenVariantId();
    if (!variantId) return;

    /* Nothing to do — do not spend one of the reconciler's 12 ops on a no-op.
       Only ever true for a claimed line: a placeholder has no current variant,
       so every pick from one is a genuine add. */
    const currentVariantId = this.trigger?.dataset.currentVariantId || '';
    if (currentVariantId && String(variantId) === String(currentVariantId)) {
      this.close();
      return;
    }

    this.isSubmitting = true;
    this.setSubmitBusy(button, true);
    this.showError('');

    const body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', '1');
    body.append('properties[_cart_tier_gift]', 'true');
    body.append('properties[_cart_tier_id]', this.tierId);
    body.append('properties[_cart_tier_swapped]', 'true');

    try {
      /* withSections false, then true: the drawer re-renders once, on the
         final operation — the same idiom applyPlan() uses. */
      await window.Cart.add(body, SOURCE, { withSections: false });

      if (this.lineKey) {
        await window.Cart.change({ id: this.lineKey, quantity: 0 }, SOURCE);
      }

      this.isSubmitting = false;
      this.close();
    } catch (error) {
      this.isSubmitting = false;
      this.setSubmitBusy(button, false);
      this.showError(error?.description || error?.message || this.errorLabel);
    }
  }

  setSubmitBusy(button, busy) {
    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');

    const label = button.querySelector('[data-gift-swap-submit-label]');
    if (!label) return;
    label.textContent = busy
      ? button.dataset.addingLabel || label.textContent
      : button.dataset.label || label.textContent;
  }

  showError(message) {
    const slot = this.activeChoice()?.querySelector('[data-gift-swap-error]');
    if (slot) slot.textContent = message;
  }

  /* -------------------------------------------------------------- gallery */

  slides() {
    return this.activeChoice()?.querySelector('[data-gift-swap-slides]') || null;
  }

  attachSlideListeners() {
    this.detachSlideListeners();
    const slides = this.slides();
    if (!slides) return;
    this.boundSlides = slides;
    slides.addEventListener('scroll', this.onSlidesScroll, { passive: true });
  }

  detachSlideListeners() {
    if (!this.boundSlides) return;
    this.boundSlides.removeEventListener('scroll', this.onSlidesScroll);
    this.boundSlides = null;
  }

  goToSlide(index) {
    const slides = this.slides();
    if (!slides) return;
    const slide = slides.querySelectorAll('[data-gift-swap-slide]')[index];
    if (!slide) return;

    slides.scrollTo({
      left: slide.offsetLeft - slides.offsetLeft,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
    this.syncGalleryControls(index);
  }

  /* The list is a scroll-snap container, so the controls follow the scroll
     position rather than the other way round — swipe and click stay in sync. */
  onSlidesScroll() {
    const slides = this.slides();
    if (!slides) return;
    const index = Math.round(slides.scrollLeft / Math.max(slides.clientWidth, 1));
    this.syncGalleryControls(index);
  }

  /** Thumbs (>=768px) and dots (<768px) are the same control, kept in step. */
  syncGalleryControls(index) {
    const choice = this.activeChoice();
    if (!choice) return;
    choice.querySelectorAll('[data-gift-swap-thumb], [data-gift-swap-dot]').forEach((control) => {
      const isSelected = parseInt(control.dataset.index, 10) === index;
      control.classList.toggle('is-selected', isSelected);
    });

    /* Every route to a new slide (thumb, dot, swipe) lands here, so this is
       the one place the wishlist button needs hiding over video. */
    const slide = choice.querySelectorAll('[data-gift-swap-slide]')[index];
    const isVideo =
      slide?.dataset.mediaType === 'video' || slide?.dataset.mediaType === 'external_video';
    choice
      .querySelector('[data-gift-swap-save]')
      ?.classList.toggle('gift-swap__save--hidden', Boolean(isVideo));
  }

  activateEmbed(choice) {
    if (!choice) return;
    const wrapper = choice.querySelector('[data-gift-swap-embed]');
    const url = wrapper?.dataset.embedUrl;
    if (!wrapper || !url || wrapper.querySelector('.gift-swap__iframe')) return;

    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.className = 'gift-swap__iframe';
    iframe.setAttribute(
      'allow',
      'accelerometer; encrypted-media; gyroscope; picture-in-picture; web-share'
    );
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('title', choice.querySelector('.gift-swap__title')?.textContent || '');

    wrapper.appendChild(iframe);
  }

  /* Stop anything audible before the slide it lives on goes away. Injected
     iframes are removed outright — pausing a cross-origin embed is not
     possible without that provider's JS API, which this module avoids. */
  stopMedia() {
    this.querySelectorAll('video').forEach((video) => {
      if (!video.paused) video.pause();
    });
    this.querySelectorAll('.gift-swap__iframe').forEach((iframe) => iframe.remove());
  }

  /* ---------------------------------------------------------------- rating */

  /**
   * Re-render the Yotpo bottomline for the colour now on screen.
   *
   * Mirrors the pattern documented in snippets/color-swatches.liquid, with two
   * additions that version does not need: it clears the stale legacy widget
   * (that file's repointStaleWidgets only handles `.yotpo-widget-instance`,
   * never `.yotpo.bottomLine`), and it injects the loader when Yotpo is absent
   * — which is the normal case off the product templates.
   *
   * initWidgets() re-renders every Yotpo widget on the page, so it must run
   * exactly once per open/colour change; retries only wait for the API to
   * arrive, they never force a second render.
   */
  refreshRating() {
    const widget = this.activeChoice()?.querySelector('.yotpo.bottomLine');
    if (!widget) return;

    /* Yotpo fills the div in place and will not re-render a populated one. */
    widget.innerHTML = '';
    widget.removeAttribute('data-yotpo-initialized');
    widget.removeAttribute('data-widget-initialized');

    let attempts = 0;
    const MAX_ATTEMPTS = 15; // ~3s at 200ms

    const tryInitOnce = () => {
      attempts += 1;
      try {
        const container = window.yotpoWidgetsContainer;
        if (container && typeof container.initWidgets === 'function') {
          container.initWidgets();
          return;
        }
        if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
          window.yotpo.refreshWidgets();
          return;
        }
      } catch (error) {
        console.warn('[cart-gift-swap] Yotpo refresh failed:', error.message);
        return;
      }

      injectYotpoLoader(this.yotpoKey);
      /* Stop once detached: a pending retry would fire initWidgets() after the
         modal closed and needlessly re-render the page's other widgets. */
      if (attempts < MAX_ATTEMPTS && this.isConnected) {
        this.ratingTimer = setTimeout(tryInitOnce, 200);
      }
    };

    clearTimeout(this.ratingTimer);
    tryInitOnce();
  }
}

/* ------------------------------------------------------------- utilities */

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * @returns {Map<string, string>} upper-cased code -> label
 */
function readSizeLabels() {
  const labels = window.theme?.sizeDisplayLabels;
  if (!labels) return new Map();
  return new Map(Object.entries(labels));
}

/* Yotpo's app block only runs on product templates, so on every other page the
   drawer has to bring the widget script itself. Once per page load. */
let yotpoLoaderInjected = false;

function injectYotpoLoader(key) {
  if (yotpoLoaderInjected || !key) return;
  yotpoLoaderInjected = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://staticw2.yotpo.com/${key}/widget.js`;
  document.head.appendChild(script);
}

/* Sizes are merchant-authored option values ("3/4", "2XL"), so they can carry
   characters that are not valid in a selector. */
function cssEscape(value) {
  if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
  return String(value).replace(/["\\]/g, '\\$&');
}

let scrollLocks = 0;
let previousOverflow = '';

/* The drawer sets body overflow too; count locks so closing the picker never
   leaves the page unscrollable behind an still-open drawer. */
function lockBodyScroll() {
  if (scrollLocks === 0) previousOverflow = document.body.style.overflow;
  scrollLocks += 1;
  document.body.style.overflow = 'hidden';
}

function releaseBodyScroll() {
  if (scrollLocks === 0) return;
  scrollLocks -= 1;
  if (scrollLocks === 0) document.body.style.overflow = previousOverflow;
}

if (!customElements.get('cart-gift-swap')) {
  customElements.define('cart-gift-swap', CartGiftSwap);
}

/* Registered at module-execution time so it survives every drawer re-render
   (the buttons are replaced, this listener is not). */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-gift-swap-trigger]');
  if (!trigger) return;

  const template = trigger.parentElement?.querySelector('[data-gift-swap-template]');
  if (!template) return;

  event.preventDefault();

  if (openInstance) openInstance.close();

  const picker = document.createElement('cart-gift-swap');
  picker.render(template, trigger);
  picker.open();
});
